import { makeAutoObservable, runInAction } from 'mobx'
import agent from '../api/agent'
import type { FlagDto, FlagListParams } from '../models/flag'
import type { ModerationLogDto, ModerationLogParams, ModeratorSummaryDto } from '../models/moderation'
import type { PagedResult } from '../models/pagedResult'

export default class ModerationStore {
  flags: FlagDto[] = []
  selectedFlag: FlagDto | null = null
  loading = false
  submitting = false
  filters: FlagListParams = { status: 0 }

  moderationLog: PagedResult<ModerationLogDto> | null = null
  logFilters: ModerationLogParams = { pageNumber: 1, pageSize: 25 }
  loadingLog = false

  moderatorSummary: ModeratorSummaryDto[] = []
  loadingSummary = false

  escalations: FlagDto[] = []
  loadingEscalations = false
  selectedEscalation: FlagDto | null = null

  /** Open (unresolved) flags queue — moderators only. */
  openFlagCount = 0

  constructor () {
    makeAutoObservable(this)
  }

  get groupedFlags (): FlagDto[] {
    const seen = new Map<string, FlagDto>()
    this.flags.forEach((f) => {
      const key = `${f.targetType}-${f.targetId}`
      if (!seen.has(key)) seen.set(key, f)
    })
    return Array.from(seen.values())
  }

  loadOpenFlagCount = async () => {
    try {
      const n = await agent.Flags.openCount()
      runInAction(() => {
        this.openFlagCount = typeof n === 'number' ? n : 0
      })
    } catch {
      runInAction(() => {
        this.openFlagCount = 0
      })
    }
  }

  loadFlags = async (params?: FlagListParams) => {
    this.loading = true
    try {
      const list = await agent.Flags.list(params ?? this.filters)
      runInAction(() => {
        this.flags = list ?? []
        this.loading = false
      })
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  selectFlag = (flag: FlagDto | null) => {
    this.selectedFlag = flag
  }

  resolveFlag = async (id: string, action: string, resolution: string) => {
    this.submitting = true
    try {
      await agent.Flags.resolve(id, { action, resolution })
      runInAction(() => {
        this.flags = this.flags.filter((f) => f.id !== id)
        if (this.selectedFlag?.id === id) this.selectedFlag = null
        this.submitting = false
      })
      void this.loadOpenFlagCount()
    } catch (error) {
      runInAction(() => (this.submitting = false))
      throw error
    }
  }

  setFilter = (key: string, value: unknown) => {
    const next = { ...this.filters, [key]: value }
    this.filters = next
    this.loadFlags(next)
  }

  loadModerationLog = async (params?: ModerationLogParams) => {
    this.loadingLog = true
    try {
      const result = await agent.Moderation.getLog(params ?? this.logFilters)
      runInAction(() => {
        this.moderationLog = result ?? null
        this.loadingLog = false
      })
    } catch (error) {
      runInAction(() => (this.loadingLog = false))
      throw error
    }
  }

  setLogFilter = (key: string, value: unknown) => {
    const next = { ...this.logFilters, [key]: value }
    this.logFilters = next
    this.loadModerationLog(next)
  }

  setLogFilters = (updates: Partial<ModerationLogParams>) => {
    const next = { ...this.logFilters, ...updates }
    this.logFilters = next
    this.loadModerationLog(next)
  }

  exportLog = async (params?: ModerationLogParams) => {
    const response = await agent.Moderation.exportLog(params ?? this.logFilters)
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `moderation-log-${new Date().toISOString().slice(0, 10)}.csv`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  loadModeratorSummary = async () => {
    this.loadingSummary = true
    try {
      const list = await agent.Moderation.getModeratorSummary()
      runInAction(() => {
        this.moderatorSummary = list ?? []
        this.loadingSummary = false
      })
    } catch (error) {
      runInAction(() => (this.loadingSummary = false))
      throw error
    }
  }

  loadEscalations = async () => {
    this.loadingEscalations = true
    try {
      const list = await agent.Moderation.getEscalations()
      runInAction(() => {
        this.escalations = list ?? []
        this.loadingEscalations = false
      })
    } catch (error) {
      runInAction(() => (this.loadingEscalations = false))
      throw error
    }
  }

  selectEscalation = (flag: FlagDto | null) => {
    this.selectedEscalation = flag
  }

  resolveEscalation = async (flagId: string, action: string, notes: string) => {
    this.submitting = true
    try {
      await agent.Moderation.resolveEscalation(flagId, { action, notes })
      runInAction(() => {
        this.escalations = this.escalations.filter((f) => f.id !== flagId)
        if (this.selectedEscalation?.id === flagId) this.selectedEscalation = null
        this.submitting = false
      })
    } catch (error) {
      runInAction(() => (this.submitting = false))
      throw error
    }
  }
}
